import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useMe } from "../hooks/useMe";
import Sidebar from "../components/Sidebar";
import API from "../APIs/API";
import "../styles/profile.scss";
import "../styles/list.scss";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { ADMIN_LOCATION_LEVEL_USER_OPTIONS } from "../constants";

// scope: 'main' | 'non_teachers' | 'teachers' | 'agency'
const EventView = ({ scope, type }) => {
  const { event_id, admin_location_id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { permissions, user: loggedUser } = useMe();

  const [editable, setEditable] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [form, setForm] = useState({ name: "", fromDate: null, toDate: null, recurring: false });

  const pageUserType = type === "Teacher" ? "teachers" : "non-teachers";

  const backUrl =
    scope === "main" ? "/main-events" :
    scope === "non_teachers" ? "/non-teachers-events" :
    scope === "teachers" ? "/teachers-events" :
    `/${pageUserType}/${admin_location_id}/events`;

  // Can the logged-in user edit events at this scope?
  const canWrite = (() => {
    if (scope === "main") return permissions.superRead;
    if (scope === "non_teachers") return permissions.superRead || permissions.non_teacher;
    if (scope === "teachers") return permissions.superRead || permissions.teacher;
    if (scope === "agency") {
      if (permissions.superRead || permissions.topLevel) return true;
      if (loggedUser?.admin_location_level_user === ADMIN_LOCATION_LEVEL_USER_OPTIONS.ADMIN_LOCATION_USER) return true;
      return false;
    }
    return false;
  })();

  const { data: eventData, isLoading } = useQuery(["event", event_id], {
    queryFn: async () => {
      const res = await new API().getEventById(event_id);
      return res?.data ?? null;
    },
  });

  // Inherited events (scope != this page's scope) are view-only
  const isInherited = eventData && eventData.scope !== scope;
  const allowEdit = canWrite && !isInherited;

  useEffect(() => {
    if (eventData) {
      setForm({
        name: eventData.name,
        fromDate: dayjs(eventData.from_date),
        toDate: dayjs(eventData.to_date),
        recurring: eventData.recurring,
      });
    }
  }, [eventData]);

  const updateMutation = useMutation(
    (data) => new API().updateEvent(event_id, data),
    {
      onSuccess: (res) => {
        if (res?.status === "ok") {
          toast.success("Event updated!");
          queryClient.invalidateQueries(["event", event_id]);
          queryClient.invalidateQueries(["events"]);
          queryClient.invalidateQueries(["events_effective"]);
          setEditable(false);
        } else {
          toast.error("Update failed.");
        }
      },
      onError: () => toast.error("Update failed."),
    }
  );

  const deleteMutation = useMutation(
    () => new API().deleteEvent(event_id),
    {
      onSuccess: () => {
        toast.success("Event deleted.");
        queryClient.invalidateQueries(["events"]);
        queryClient.invalidateQueries(["events_effective"]);
        navigate(backUrl);
      },
      onError: () => toast.error("Delete failed."),
    }
  );

  const handleSave = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.warning("Event name is required."); return; }
    if (!form.fromDate || !form.toDate) { toast.warning("Dates are required."); return; }
    if (dayjs(form.toDate).isBefore(dayjs(form.fromDate))) {
      toast.warning("To date must be on or after From date."); return;
    }
    updateMutation.mutate({
      name: form.name.trim(),
      from_date: dayjs(form.fromDate).format("YYYY-MM-DD"),
      to_date: dayjs(form.toDate).format("YYYY-MM-DD"),
      recurring: form.recurring,
    });
  };

  const handleCancel = () => {
    if (eventData) {
      setForm({
        name: eventData.name,
        fromDate: dayjs(eventData.from_date),
        toDate: dayjs(eventData.to_date),
        recurring: eventData.recurring,
      });
    }
    setEditable(false);
  };

  const formatDate = (d) => d ? dayjs(d).format("DD/MM/YYYY") : "";

  const scopeLabel = (s) => ({
    main: "Main", non_teachers: "Non-Teachers", teachers: "Teachers", agency: "Agency",
  })[s] ?? s;

  if (isLoading) return (
    <div className="wrapper">
      <div className="sidebar"><Sidebar /></div>
      <div className="content"><div className="profile"><div className="profileContainer"><p style={{ padding: 32 }}>Loading…</p></div></div></div>
    </div>
  );

  return (
    <div className="wrapper">
      <div className="sidebar"><Sidebar /></div>
      <div className="content">
        <div className="profile">
          <div className="profileContainer">
            <div className="profileSection">
              <Link to={backUrl} className="backButtonSection">
                <KeyboardBackspaceIcon className="backButton" />
              </Link>
              <br /><br />

              <form onSubmit={handleSave}>
                <div className="container">
                  <div className="row">
                    <div className="col col-sm">
                      <h1>Event Details</h1>
                      {isInherited && (
                        <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 10px", borderRadius: 10, backgroundColor: "#f3e8ff", color: "#6d28d9", marginLeft: 8 }}>
                          Inherited from {scopeLabel(eventData?.scope)}
                        </span>
                      )}
                    </div>
                  </div>
                  <hr />

                  <div className="row mt-3">
                    <div className="col col-sm-6">
                      <div className="form-outline">
                        <label className="form-label"><b>Event Name</b></label>
                        <br />
                        <input
                          className="form-control"
                          type="text"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          disabled={!editable}
                        />
                      </div>
                    </div>
                    <div className="col col-sm-6">
                      <div className="form-outline">
                        <label className="form-label"><b>Created By</b></label>
                        <br />
                        <input
                          className="form-control"
                          type="text"
                          value={eventData?.created_by ?? ""}
                          disabled
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row mt-3">
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label"><b>From Date</b></label>
                        <br />
                        <DatePicker
                          value={form.fromDate}
                          onChange={(v) => setForm({ ...form, fromDate: v })}
                          disabled={!editable}
                          renderInput={(params) => (
                            <TextField {...params} size="small" fullWidth className="form-control" style={{ padding: 0 }} />
                          )}
                          inputFormat="DD/MM/YYYY"
                        />
                      </div>
                    </div>
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label"><b>To Date</b></label>
                        <br />
                        <DatePicker
                          value={form.toDate}
                          onChange={(v) => setForm({ ...form, toDate: v })}
                          disabled={!editable}
                          minDate={form.fromDate}
                          renderInput={(params) => (
                            <TextField {...params} size="small" fullWidth className="form-control" style={{ padding: 0 }} />
                          )}
                          inputFormat="DD/MM/YYYY"
                        />
                      </div>
                    </div>
                    <div className="col col-sm-4">
                      <div className="form-outline">
                        <label className="form-label"><b>Scope</b></label>
                        <br />
                        <input
                          className="form-control"
                          type="text"
                          value={scopeLabel(eventData?.scope)}
                          disabled
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row mt-3">
                    <div className="col col-sm-12">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.recurring}
                            onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
                            disabled={!editable}
                            color="secondary"
                          />
                        }
                        label={<span style={{ fontWeight: 600 }}>Recurring annually</span>}
                      />
                      <p style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                        {form.recurring
                          ? "This event repeats every year on the same date(s)."
                          : "This event occurs once only."}
                      </p>
                    </div>
                  </div>

                  <div className="row mt-3">
                    <div className="col col-sm-6">
                      <div className="form-outline">
                        <label className="form-label"><b>Created At</b></label>
                        <br />
                        <input className="form-control" type="text" value={formatDate(eventData?.created_at)} disabled />
                      </div>
                    </div>
                    <div className="col col-sm-6">
                      <div className="form-outline">
                        <label className="form-label"><b>Updated At</b></label>
                        <br />
                        <input className="form-control" type="text" value={formatDate(eventData?.updated_at)} disabled />
                      </div>
                    </div>
                  </div>

                  <hr />

                  {/* Button row */}
                  {allowEdit && (
                    <div className="row" style={{ justifyContent: "flex-end", gap: 8, display: "flex", flexWrap: "wrap" }}>
                      {editable ? (
                        <>
                          <button type="button" className="saveBtn btn btn-secondary" onClick={handleCancel}>Cancel</button>
                          <button type="submit" className="saveBtn btn btn-primary" disabled={updateMutation.isLoading}>
                            {updateMutation.isLoading ? "Saving…" : "Save Changes"}
                          </button>
                        </>
                      ) : (
                        <button type="button" className="saveBtn btn btn-warning" onClick={() => setEditable(true)}>Edit</button>
                      )}
                    </div>
                  )}

                  {/* Delete */}
                  {allowEdit && !editable && (
                    <div style={{ marginTop: 32, paddingTop: 20, borderTop: "1px solid #f3e8ff" }}>
                      {!showDeleteConfirm ? (
                        <button
                          type="button"
                          className="saveBtn btn btn-danger"
                          onClick={() => setShowDeleteConfirm(true)}
                        >
                          Delete Event
                        </button>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <span style={{ fontSize: 14, color: "#dc2626" }}>Are you sure? This cannot be undone.</span>
                          <button type="button" className="saveBtn btn btn-secondary" onClick={() => setShowDeleteConfirm(false)}>No</button>
                          <button
                            type="button"
                            className="saveBtn btn btn-danger"
                            onClick={() => deleteMutation.mutate()}
                            disabled={deleteMutation.isLoading}
                          >
                            {deleteMutation.isLoading ? "Deleting…" : "Yes, Delete"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventView;
